import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Component to display a clear summary of team license usage
 * Shows active members, pending invitations, and total usage against plan limit
 */
const TeamLicenseUsageSummary = ({ activeMembers = 0, pendingInvitations = 0, planLimit = 5 }) => {
  // Log props for debugging
  useEffect(() => {
    console.log('TeamLicenseUsageSummary props:', { activeMembers, pendingInvitations, planLimit });
  }, [activeMembers, pendingInvitations, planLimit]);
  
  const totalUsed = (activeMembers || 0) + (pendingInvitations || 0);
  const usagePercentage = planLimit > 0 ? Math.round((totalUsed / planLimit) * 100) : 0;
  
  // Determine progress bar color based on usage percentage
  const getProgressColor = () => {
    if (usagePercentage >= 100) return 'bg-red-500 dark:bg-red-600';
    if (usagePercentage >= 80) return 'bg-yellow-500 dark:bg-yellow-600';
    return 'bg-blue-500 dark:bg-blue-600';
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Team License Usage
      </h2>
      
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {totalUsed} of {planLimit} licenses used ({usagePercentage}%)
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {planLimit - totalUsed} licenses remaining
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${getProgressColor()}`} 
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm border border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
          {activeMembers} Active Members
        </div>
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm border border-purple-500 dark:border-purple-400 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          {pendingInvitations} Pending Invitations
        </div>
      </div>
      
      {usagePercentage >= 80 && (
        <div className="mt-4">
          <Link 
            to="/subscription"
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${usagePercentage >= 100 ? 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800' : 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            {usagePercentage >= 100 ? "Upgrade Now" : "Consider Upgrading"}
          </Link>
        </div>
      )}
    </div>
  );
};

export default TeamLicenseUsageSummary;
